/**
 * Notes UI state:
 * - Read: localStorage (per user bucket) — unchanged.
 * - Flag + journal + feedback presence: Laravel API (`synapse_2025` routes) + reactive in-memory cache.
 */

import Vue from 'vue';
import { get, byMethod } from './api';

function userKey(store) {
  try {
    const u = store && store.getters && store.getters.user;
    if (u && u.id != null) return String(u.id);
  } catch (e) {
    /* ignore */
  }
  try {
    const raw = localStorage.getItem('vuex');
    if (raw) {
      const j = JSON.parse(raw);
      const id = j && j.user && j.user.id;
      if (id != null) return String(id);
    }
  } catch (e) {
    /* ignore */
  }
  return 'anon';
}

function storageKey(store) {
  return `notesUserPrefs_${userKey(store)}`;
}

let prefsCacheKey = null;
let prefsCache = null;

function loadPrefs(store) {
  try {
    const raw = localStorage.getItem(storageKey(store));
    if (!raw) return { read: {}, chapterFlags: {}, highlights: {} };
    const j = JSON.parse(raw);
    return {
      read: j.read || {},
      chapterFlags: j.chapterFlags || {},
      highlights: j.highlights || {},
    };
  } catch (e) {
    return { read: {}, chapterFlags: {}, highlights: {} };
  }
}

function getCachedPrefs(store) {
  const key = storageKey(store);
  if (prefsCacheKey === key && prefsCache) return prefsCache;
  prefsCacheKey = key;
  prefsCache = loadPrefs(store);
  return prefsCache;
}

function invalidatePrefsCache() {
  prefsCacheKey = null;
  prefsCache = null;
}

function savePrefs(store, prefs) {
  prefsCache = prefs;
  prefsCacheKey = storageKey(store);
  try {
    localStorage.setItem(
      storageKey(store),
      JSON.stringify({
        read: prefs.read || {},
        chapterFlags: prefs.chapterFlags || {},
        highlights: prefs.highlights || {},
      }),
    );
  } catch (e) {
    /* ignore */
  }
}

function readStorageKey(type, id, number) {
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const nk = normalizeNoteNumber(number) || idToNumber[recordKey(t, id)];
  if (nk) return `num:${nk}`;
  if (id == null || id === '') return '';
  return recordKey(t, id);
}

export function recordKey(type, id) {
  return `${type}:${String(id)}`;
}

export function normalizeNoteNumber(number) {
  // Match API `number` codes only (e.g. ACP002 / ACP006) — never index_number.
  if (number == null || String(number).trim() === '') return '';
  return String(number).trim().toUpperCase();
}

/** Shared UI revision so every NotesSubList refreshes when prefs change. */
export const notePrefsUi = Vue.observable({ rev: 0 });

export function bumpNotePrefsUi() {
  notePrefsUi.rev += 1;
}

/** Call when another tab updates notesUserPrefs_* in localStorage. */
export function invalidateNotesPrefsCache() {
  invalidatePrefsCache();
}

/** Clear all note read marks (and local chapter flag toggles). Keeps journals/highlights. */
export function clearAllNotesReadProgress(store) {
  const prefs = getCachedPrefs(store);
  prefs.read = {};
  prefs.chapterFlags = {};
  savePrefs(store, prefs);
  bumpNotePrefsUi();
}

/** note number (ACP…) -> [{ type, id }] */
const numberToRecords = {};
/** recordKey(type,id) -> normalized note number */
const idToNumber = {};
/** Last list trees used to rebuild peer lookup */
let lastNumberDatasets = [];

function addNumberRecord(type, id, number) {
  if (id == null || id === '') return;
  const nk = normalizeNoteNumber(number);
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const ik = recordKey(t, id);
  if (nk) idToNumber[ik] = nk;
  if (!nk) return;
  if (!numberToRecords[nk]) numberToRecords[nk] = [];
  if (!numberToRecords[nk].some((r) => recordKey(r.type, r.id) === ik)) {
    numberToRecords[nk].push({ type: t, id });
  }
}

function walkItemsForNumberRecords(items, listMode = 'chapter') {
  const list = [];
  if (!Array.isArray(items)) return list;

  const pushLeaf = (type, node) => {
    if (!node || node.id == null) return;
    // Only the API `number` field (ACP002…) — ignore index_number.
    const code = node.number;
    list.push({ type, id: node.id, number: code });
  };

  if (listMode === 'flat') {
    for (const item of items) {
      if (item.children && item.children.length) {
        for (const sub of item.children) pushLeaf('subnotes', sub);
      } else {
        pushLeaf('notes', item);
      }
    }
    return list;
  }

  for (const chapter of items) {
    for (const note of chapter.children || []) {
      if (note.children && note.children.length) {
        for (const sub of note.children) pushLeaf('subnotes', sub);
      } else {
        pushLeaf('notes', note);
      }
    }
  }
  return list;
}

function rebuildMapsFromDatasets(datasets) {
  Object.keys(numberToRecords).forEach((k) => delete numberToRecords[k]);
  Object.keys(idToNumber).forEach((k) => delete idToNumber[k]);
  if (!Array.isArray(datasets)) return;
  for (const entry of datasets) {
    const items = entry && entry.items;
    const listMode = (entry && entry.listMode) || 'chapter';
    for (const rec of walkItemsForNumberRecords(items, listMode)) {
      addNumberRecord(rec.type, rec.id, rec.number);
    }
  }
}

/** Build cross-chapter lookup so prefs sync by API `number` (e.g. ACP001). */
export function rebuildNotesNumberRegistry(datasets) {
  lastNumberDatasets = Array.isArray(datasets) ? datasets : [];
  rebuildMapsFromDatasets(lastNumberDatasets);
}

/** Register a single note/subnote (e.g. when opening a note page). */
export function registerNoteRecord(type, id, number) {
  addNumberRecord(type, id, number);
}

/**
 * All notes/subnotes that share the same API `number` code (ACP002…).
 * Does not use index_number.
 *
 * IMPORTANT: do not rebuild maps here. List render calls this for every
 * flag/read icon; rebuilding the full tree on each lookup freezes the UI.
 * Maps are updated via rebuildNotesNumberRegistry / registerNoteRecord.
 */
export function getRecordsForRecord(type, id, number) {
  if (id == null) return [];
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const nk = normalizeNoteNumber(number) || idToNumber[recordKey(t, id)];
  if (!nk || !numberToRecords[nk] || !numberToRecords[nk].length) {
    return [{ type: t, id }];
  }
  return numberToRecords[nk];
}

/** Debug/helper: peers for an ACP code. */
export function getRecordsForNumber(number) {
  const nk = normalizeNoteNumber(number);
  if (!nk || !numberToRecords[nk]) return [];
  return numberToRecords[nk].map((r) => ({ ...r }));
}

function setReadStateForRecordAndPeers(store, type, id, read, number) {
  if (id == null || id === '') return;
  const prefs = getCachedPrefs(store);
  const k = readStorageKey(type, id, number);
  if (!k) return;
  if (read) prefs.read[k] = true;
  else delete prefs.read[k];
  savePrefs(store, prefs);
  bumpNotePrefsUi();
}

/** Reactive cache for flags + journal + feedback presence (Vue 2 reactivity). */
export const noteUserApiCache = Vue.observable({
  version: 0,
  flagMap: {},
  journalNonemptyMap: {},
  feedbackNonemptyMap: {},
});

const journalHtmlByKey = {};
let hydrateInFlight = null;

function bumpApiVersion() {
  noteUserApiCache.version += 1;
}

export function hydrateNoteUserState() {
  if (hydrateInFlight) return hydrateInFlight;
  hydrateInFlight = get('/note-user-state')
    .then((res) => {
      const nextFlags = {};
      (res.data?.data?.flags || []).forEach((k) => {
        nextFlags[k] = true;
      });
      const nextJ = {};
      (res.data?.data?.journal_nonempty || []).forEach((k) => {
        nextJ[k] = true;
      });
      const nextFb = {};
      (res.data?.data?.feedback_nonempty || []).forEach((k) => {
        nextFb[k] = true;
      });
      noteUserApiCache.flagMap = nextFlags;
      noteUserApiCache.journalNonemptyMap = nextJ;
      noteUserApiCache.feedbackNonemptyMap = nextFb;
      bumpApiVersion();
    })
    .catch(() => {
      /* offline / 401 — leave cache */
    })
    .finally(() => {
      hydrateInFlight = null;
    });
  return hydrateInFlight;
}

function normalizeFlagType(type) {
  if (type === 'subnotes') return 'subnotes';
  if (type === 'chapters') return 'chapters';
  return 'notes';
}

export function applyFlagToCache(type, id, flagged) {
  const k = recordKey(normalizeFlagType(type), id);
  if (flagged) Vue.set(noteUserApiCache.flagMap, k, true);
  else Vue.delete(noteUserApiCache.flagMap, k);
  bumpApiVersion();
}

export function applyJournalNonemptyToCache(type, id, hasContent) {
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const k = recordKey(t, id);
  if (hasContent) Vue.set(noteUserApiCache.journalNonemptyMap, k, true);
  else Vue.delete(noteUserApiCache.journalNonemptyMap, k);
  bumpApiVersion();
}

export function applyFeedbackToCache(type, id, hasFeedback) {
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const k = recordKey(t, id);
  if (hasFeedback) Vue.set(noteUserApiCache.feedbackNonemptyMap, k, true);
  else Vue.delete(noteUserApiCache.feedbackNonemptyMap, k);
  bumpApiVersion();
}

/** notes | subnotes — also syncs every record that shares the same `number`. */
export function markNoteVisitedAsRead(store, noteId, recordType, number) {
  setReadStateForRecordAndPeers(store, recordType, noteId, true, number);
}

/** Clear read state for a note or subnote (toolbar "unread"). */
export function markNoteUnread(store, noteId, recordType, number) {
  setReadStateForRecordAndPeers(store, recordType, noteId, false, number);
}

export function isRecordRead(store, type, id, number) {
  void notePrefsUi.rev;
  if (id == null) return false;
  const prefs = getCachedPrefs(store);
  const k = readStorageKey(type, id, number);
  if (k && prefs.read[k]) return true;
  // Legacy id-based entries written before number-key sync.
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  if (prefs.read[recordKey(t, id)]) return true;
  const nk = normalizeNoteNumber(number) || idToNumber[recordKey(t, id)];
  if (!nk) return false;
  const peers = numberToRecords[nk];
  if (!peers || !peers.length) return false;
  return peers.some((r) => !!prefs.read[recordKey(r.type, r.id)]);
}

export async function toggleRecordFlag(store, type, id, number) {
  if (id == null) return false;
  const peers = getRecordsForRecord(type, id, number);
  const anyFlagged = peers.some((r) => {
    const t = normalizeFlagType(r.type);
    return !!noteUserApiCache.flagMap[recordKey(t, r.id)];
  });
  const targetFlagged = !anyFlagged;

  await Promise.all(peers.map(async (r) => {
    const t = normalizeFlagType(r.type);
    const k = recordKey(t, r.id);
    const cur = !!noteUserApiCache.flagMap[k];
    if (cur === targetFlagged) return targetFlagged;
    const res = await byMethod('post', '/note-flag-toggle', { id: r.id, type: t });
    const flagged = !!res.data?.data?.flagged;
    applyFlagToCache(t, r.id, flagged);
    return flagged;
  }));

  bumpNotePrefsUi();
  return targetFlagged;
}

export function isRecordFlagged(store, type, id, number) {
  void noteUserApiCache.version;
  void notePrefsUi.rev;
  if (id == null) return false;
  return getRecordsForRecord(type, id, number).some((r) => {
    const t = normalizeFlagType(r.type);
    return !!noteUserApiCache.flagMap[recordKey(t, r.id)];
  });
}

export async function fetchJournalHtml(store, type, id) {
  if (id == null) return '';
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const k = recordKey(t, id);
  if (Object.prototype.hasOwnProperty.call(journalHtmlByKey, k)) {
    return journalHtmlByKey[k];
  }
  const res = await get('/note-journal', { id, type: t });
  const html = res.data?.data?.html ?? '';
  journalHtmlByKey[k] = html;
  return html;
}

export async function saveJournalHtml(store, type, id, html) {
  if (id == null) return;
  const plain = String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const hasContent = plain.length > 0;
  const peers = getRecordsForRecord(type, id);

  await Promise.all(peers.map(async (r) => {
    const t = r.type === 'subnotes' ? 'subnotes' : 'notes';
    const k = recordKey(t, r.id);
    await byMethod('post', '/note-journal', { id: r.id, type: t, html });
    journalHtmlByKey[k] = html;
    applyJournalNonemptyToCache(t, r.id, hasContent);
  }));
}

/** Sync read of cached journal HTML (use after fetchJournalHtml). */
export function getJournalHtmlFromCache(store, type, id) {
  if (id == null) return '';
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const k = recordKey(t, id);
  return journalHtmlByKey[k] != null ? journalHtmlByKey[k] : '';
}

export function journalHasContent(store, type, id) {
  void noteUserApiCache.version;
  if (id == null) return false;
  return getRecordsForRecord(type, id).some((r) => {
    const t = r.type === 'subnotes' ? 'subnotes' : 'notes';
    return !!noteUserApiCache.journalNonemptyMap[recordKey(t, r.id)];
  });
}

export function feedbackHasContent(store, type, id) {
  void noteUserApiCache.version;
  if (id == null) return false;
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  return !!noteUserApiCache.feedbackNonemptyMap[recordKey(t, id)];
}

export async function fetchFlagged(store, type, id) {
  if (id == null) return false;
  const t = normalizeFlagType(type);
  const res = await get('/note-flagged', { id, type: t });
  const flagged = !!res.data?.data?.flagged;
  applyFlagToCache(t, id, flagged);
  return flagged;
}

/** All navigable leaves under a chapter (for read / flag aggregation) */
export function chapterDescendantRecords(chapter) {
  const list = [];
  if (!chapter || !chapter.children || !chapter.children.length) return list;
  for (const note of chapter.children) {
    if (note.children && note.children.length) {
      for (const sub of note.children) {
        if (sub.id != null) list.push({ type: 'subnotes', id: sub.id, number: sub.number });
      }
    } else if (note.id != null) {
      list.push({ type: 'notes', id: note.id, number: note.number });
    }
  }
  return list;
}

export function rowDescendantRecords(row, listMode = 'chapter') {
  if (listMode === 'flat') {
    if (!row) return [];
    if (row.children && row.children.length) {
      return row.children
        .filter((c) => c.id != null)
        .map((c) => ({ type: 'subnotes', id: c.id, number: c.number }));
    }
    if (row.id != null) return [{ type: 'notes', id: row.id, number: row.number }];
    return [];
  }
  return chapterDescendantRecords(row);
}

export function rowAllRead(store, row, listMode = 'chapter') {
  const leaves = rowDescendantRecords(row, listMode);
  if (!leaves.length) return false;
  return leaves.every((r) => isRecordRead(store, r.type, r.id, r.number));
}

export function rowAnyFlagged(store, row, listMode = 'chapter') {
  if (listMode === 'flat') return noteAnyFlagged(store, row);
  return chapterAnyFlagged(store, row);
}

export function rowAnyJournal(store, row, listMode = 'chapter') {
  const leaves = rowDescendantRecords(row, listMode);
  return leaves.some((r) => journalHasContent(store, r.type, r.id));
}

export function markRowAllRead(store, row, listMode = 'chapter') {
  const leaves = rowDescendantRecords(row, listMode);
  if (!leaves.length) return;
  const prefs = getCachedPrefs(store);
  for (const leaf of leaves) {
    const k = readStorageKey(leaf.type, leaf.id, leaf.number);
    if (k) prefs.read[k] = true;
  }
  savePrefs(store, prefs);
  bumpNotePrefsUi();
}

export function markRowAllUnread(store, row, listMode = 'chapter') {
  const leaves = rowDescendantRecords(row, listMode);
  if (!leaves.length) return;
  const prefs = getCachedPrefs(store);
  for (const leaf of leaves) {
    const k = readStorageKey(leaf.type, leaf.id, leaf.number);
    if (k) delete prefs.read[k];
  }
  savePrefs(store, prefs);
  bumpNotePrefsUi();
}

/** Mark every note and subnote under a chapter as read (bulk action from list). */
export function markChapterAllRead(store, chapter) {
  markRowAllRead(store, chapter, 'chapter');
}

/** Clear read state for every note and subnote under a chapter (bulk action from list). */
export function markChapterAllUnread(store, chapter) {
  markRowAllUnread(store, chapter, 'chapter');
}

export function chapterAllRead(store, chapter) {
  return rowAllRead(store, chapter, 'chapter');
}

export function isChapterDirectFlagged(store, chapter) {
  if (!chapter || chapter.id == null) return false;
  const prefs = getCachedPrefs(store);
  return !!prefs.chapterFlags[recordKey('chapters', chapter.id)];
}

export function chapterChildrenAnyFlagged(store, chapter) {
  if (!chapter || !chapter.children || !chapter.children.length) return false;
  return chapter.children.some((note) => noteAnyFlagged(store, note));
}

/** Chapter row shows flagged when the chapter itself or any note/subnote is flagged. */
export function chapterAnyFlagged(store, chapter) {
  if (!chapter) return false;
  if (isChapterDirectFlagged(store, chapter)) return true;
  return chapterChildrenAnyFlagged(store, chapter);
}

/** Toggle chapter-only flag (stored locally; API supports notes/subnotes only). */
export function toggleChapterFlag(store, chapterId) {
  if (chapterId == null) return false;
  const prefs = getCachedPrefs(store);
  const k = recordKey('chapters', chapterId);
  const next = !prefs.chapterFlags[k];
  if (next) prefs.chapterFlags[k] = true;
  else delete prefs.chapterFlags[k];
  savePrefs(store, prefs);
  return next;
}

export function chapterAnyJournal(store, chapter) {
  const leaves = chapterDescendantRecords(chapter);
  return leaves.some((r) => journalHasContent(store, r.type, r.id));
}

/** All navigable note/subnote leaves across multiple chapter trees */
export function allLeavesFromChapters(chapters) {
  if (!Array.isArray(chapters)) return [];
  const list = [];
  for (const chapter of chapters) {
    list.push(...chapterDescendantRecords(chapter));
  }
  return list;
}

/** Dedupe key: prefer API note number, fall back to peer registry / record id. */
function resolveLeafNumberKey(leaf) {
  const t = leaf.type === 'subnotes' ? 'subnotes' : 'notes';
  const ik = recordKey(t, leaf.id);
  let nk = normalizeNoteNumber(leaf.number) || idToNumber[ik];
  if (!nk) {
    for (const [num, recs] of Object.entries(numberToRecords)) {
      if (recs.some((r) => recordKey(r.type, r.id) === ik)) {
        nk = num;
        break;
      }
    }
  }
  return nk ? `num:${nk}` : ik;
}

/** One entry per unique note number (ACP001…) or per record id when number is missing. */
function uniqueNoteLeaves(leaves) {
  const seen = new Set();
  const unique = [];
  for (const leaf of leaves || []) {
    const key = resolveLeafNumberKey(leaf);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(leaf);
  }
  return unique;
}

/** Conditions list: one progress unit per list row (unique parent note number). */
function flatNotesProgressStats(store, items) {
  const list = Array.isArray(items) ? items : [];
  const seen = new Set();
  let total = 0;
  let read = 0;
  for (const item of list) {
    if (!item || item.id == null) continue;
    const key = resolveLeafNumberKey({ type: 'notes', id: item.id, number: item.number });
    if (seen.has(key)) continue;
    seen.add(key);
    total += 1;
    if (rowAllRead(store, item, 'flat')) read += 1;
  }
  if (!total) return { read: 0, total: 0, percent: 0 };
  return {
    read,
    total,
    percent: Math.round((read / total) * 100),
  };
}

/** Read progress for sidebar: count of marked-read leaves vs total leaves */
export function notesProgressStats(store, items, listMode = 'chapter') {
  if (listMode === 'flat') {
    return flatNotesProgressStats(store, items);
  }
  const leaves = allLeavesFromChapters(items);
  const uniqueLeaves = uniqueNoteLeaves(leaves);
  const total = uniqueLeaves.length;
  if (!total) return { read: 0, total: 0, percent: 0 };
  const read = uniqueLeaves.filter((r) => isRecordRead(store, r.type, r.id, r.number)).length;
  return {
    read,
    total,
    percent: Math.round((read / total) * 100),
  };
}

/** Navigable leaves under a single note row (leaf note or its subnotes). */
export function noteDescendantRecords(note) {
  const list = [];
  if (!note) return list;
  if (note.children && note.children.length) {
    for (const sub of note.children) {
      if (sub.id != null) list.push({ type: 'subnotes', id: sub.id, number: sub.number });
    }
  } else if (note.id != null) {
    list.push({ type: 'notes', id: note.id, number: note.number });
  }
  return list;
}

export function markNoteAllRead(store, note) {
  const leaves = noteDescendantRecords(note);
  if (!leaves.length) return;
  const prefs = getCachedPrefs(store);
  for (const leaf of leaves) {
    const k = readStorageKey(leaf.type, leaf.id, leaf.number);
    if (k) prefs.read[k] = true;
  }
  savePrefs(store, prefs);
  bumpNotePrefsUi();
}

export function markNoteAllUnread(store, note) {
  const leaves = noteDescendantRecords(note);
  if (!leaves.length) return;
  const prefs = getCachedPrefs(store);
  for (const leaf of leaves) {
    const k = readStorageKey(leaf.type, leaf.id, leaf.number);
    if (k) delete prefs.read[k];
  }
  savePrefs(store, prefs);
  bumpNotePrefsUi();
}

export function noteAnyFlagged(store, note) {
  if (!note) return false;
  if (isRecordFlagged(store, 'notes', note.id, note.number)) return true;
  if (note.children && note.children.length) {
    return note.children.some((sub) => isRecordFlagged(store, 'subnotes', sub.id, sub.number));
  }
  return false;
}

/** Note row: leaf note, or parent of subnotes — read aggregate */
export function noteReadAggregate(store, note) {
  if (!note) return false;
  if (note.children && note.children.length) {
    return note.children.every((sub) => sub.id != null && isRecordRead(store, 'subnotes', sub.id, sub.number));
  }
  return isRecordRead(store, 'notes', note.id, note.number);
}

/** Per-user text highlights for a note/subnote (local only — never writes note content tables). */
export function getNoteHighlights(store, type, id) {
  if (id == null || id === '') return [];
  const prefs = getCachedPrefs(store);
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const list = prefs.highlights[recordKey(t, id)];
  return Array.isArray(list) ? list.slice() : [];
}

export function setNoteHighlights(store, type, id, highlights) {
  if (id == null || id === '') return;
  const prefs = getCachedPrefs(store);
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  const k = recordKey(t, id);
  if (!prefs.highlights) prefs.highlights = {};
  if (!Array.isArray(highlights) || !highlights.length) {
    delete prefs.highlights[k];
  } else {
    prefs.highlights[k] = highlights;
  }
  savePrefs(store, prefs);
}

export function addNoteHighlight(store, type, id, highlight) {
  if (!highlight) return [];
  const list = getNoteHighlights(store, type, id);
  list.push(highlight);
  setNoteHighlights(store, type, id, list);
  return list;
}

export function removeNoteHighlight(store, type, id, highlightId) {
  const list = getNoteHighlights(store, type, id).filter(
    (h) => !h || String(h.id) !== String(highlightId),
  );
  setNoteHighlights(store, type, id, list);
  return list;
}
